import { api } from './api';

export type SoldierTier = 'private' | 'officer' | 'general' | 'warlord';

export interface EnlistTierPrice {
    id: SoldierTier;
    label: string;
    amount_cents: number;
    perks: string;
    rank: number;
}

export interface Soldier {
    soldier_id: number;
    handle: string;
    handle_status?: string;
    team_id: number;
    current_team_id: number;
    tier: SoldierTier;
    home_county_fips: string;
    battle_cry?: string | null;
    rank_points: number;
    founder_number?: number | null;
    recruiter_code?: string;
    avatar_url?: string | null;
    profile_links?: string[];
    times_conscripted: number;
    times_liberated: number;
    battles_survived: number;
    enlisted_at?: string;
    team_name?: string | null;
    current_team_name?: string | null;
    army_name?: string | null;
    current_army_name?: string | null;
    county_name?: string | null;
    state_name?: string | null;
    primary_color?: string | null;
    is_conscripted?: boolean;
    medals?: { medal_sku: string; source: string; awarded_at: string }[];
    service?: {
        event_type: string;
        game_id?: number;
        from_team_id?: number;
        to_team_id?: number;
        county_fips?: string;
        note?: string;
        created_at: string;
    }[];
}

export interface CheckoutDescriptor {
    configured: boolean;
    checkout_url: string | null;
    session_id?: string;
    amount_cents?: number;
    message?: string;
    dev_fulfilled?: boolean;
    invalid?: boolean;
}

function unwrap<T>(res: Awaited<ReturnType<typeof api.get>>): T {
    if (!res.success) throw new Error(res.error || 'Request failed');
    return (res.data as T) ?? (res as unknown as T);
}

export const enlistService = {
    async getPricing() {
        const res = await api.get<{
            success: boolean;
            configured: boolean;
            pricing: {
                tiers: EnlistTierPrice[];
                paid_commerce?: boolean;
                medals?: { sku: string; name: string; amount_cents: number }[];
            };
        }>('/enlist/pricing');
        return unwrap<{
            success: boolean;
            configured: boolean;
            pricing: {
                tiers: EnlistTierPrice[];
                paid_commerce?: boolean;
                medals?: { sku: string; name: string; amount_cents: number }[];
            };
        }>(res);
    },

    async getMe() {
        const res = await api.get<{ success: boolean; soldier: Soldier | null }>('/enlist/me');
        return unwrap<{ soldier: Soldier | null }>(res);
    },

    async getByHandle(handle: string) {
        const res = await api.get<{ success: boolean; soldier: Soldier }>(
            `/enlist/s/${encodeURIComponent(handle)}`
        );
        return unwrap<{ soldier: Soldier }>(res);
    },

    async enlist(payload: {
        team_id: number;
        handle: string;
        zip?: string;
        lat?: number;
        lng?: number;
        county_fips?: string;
        battle_cry?: string;
        tier?: SoldierTier;
        recruiter_code?: string;
        email?: string;
    }) {
        const res = await api.post<{
            success: boolean;
            soldier: Soldier;
            needs_checkout?: boolean;
            checkout?: CheckoutDescriptor;
            guest_token?: string;
        }>('/enlist/enlist', payload);
        return unwrap<{
            soldier: Soldier;
            needs_checkout?: boolean;
            checkout?: CheckoutDescriptor;
            guest_token?: string;
        }>(res);
    },

    async upgrade(tier: SoldierTier) {
        const res = await api.post<{ success: boolean; checkout: CheckoutDescriptor }>('/enlist/upgrade', {
            tier,
        });
        return unwrap<{ checkout: CheckoutDescriptor }>(res);
    },

    async claimGuest(guest_token: string) {
        const res = await api.post<{ success: boolean; soldier: Soldier }>('/enlist/claim', { guest_token });
        return unwrap<{ soldier: Soldier }>(res);
    },

    async getMarkers() {
        const res = await api.get<{
            success: boolean;
            markers: {
                soldier_id: number;
                handle: string;
                tier: SoldierTier;
                fips: string;
                team_id: number;
                color: string;
                lat: number | null;
                lng: number | null;
                is_conscripted: boolean;
            }[];
            unrest: { county_fips: string; unrest: number }[];
        }>('/enlist/markers');
        return unwrap<{
            markers: {
                soldier_id: number;
                handle: string;
                tier: SoldierTier;
                fips: string;
                team_id: number;
                color: string;
                lat: number | null;
                lng: number | null;
                is_conscripted: boolean;
            }[];
            unrest: { county_fips: string; unrest: number }[];
        }>(res);
    },

    async getArmies() {
        const res = await api.get<{
            success: boolean;
            armies: {
                team_id: number;
                army_name: string;
                area_code: string;
                expertise: string;
                unit: string;
                team_name: string | null;
                short_name: string | null;
            }[];
        }>('/enlist/armies');
        return unwrap<{
            armies: {
                team_id: number;
                army_name: string;
                area_code: string;
                expertise: string;
                unit: string;
                team_name: string | null;
                short_name: string | null;
            }[];
        }>(res);
    },

    async getArmyWall(teamId: number) {
        const res = await api.get<{
            success: boolean;
            wall: Soldier[];
            army: {
                team_id: number;
                army_name: string;
                area_code: string;
                expertise: string;
                unit: string;
                team_name: string | null;
                short_name: string | null;
            } | null;
        }>(`/enlist/army/${teamId}`);
        return unwrap<{
            wall: Soldier[];
            army: {
                team_id: number;
                army_name: string;
                area_code: string;
                expertise: string;
                unit: string;
                team_name: string | null;
                short_name: string | null;
            } | null;
        }>(res);
    },

    async getCounty(fips: string) {
        const res = await api.get<{
            success: boolean;
            county_fips: string;
            soldiers: Soldier[];
            sponsor: { handle: string; banner_text: string } | null;
        }>(`/enlist/county/${fips}`);
        return unwrap<{
            county_fips: string;
            soldiers: Soldier[];
            sponsor: { handle: string; banner_text: string } | null;
        }>(res);
    },

    async getLegion() {
        const res = await api.get<{
            success: boolean;
            founders: Soldier[];
            remaining: number;
        }>('/enlist/legion');
        return unwrap<{ founders: Soldier[]; remaining: number }>(res);
    },

    async getHall(teamId: number) {
        const res = await api.get<{ success: boolean; hall: unknown }>(`/enlist/hall/${teamId}`);
        return unwrap<{ hall: unknown }>(res);
    },

    async getWarRoom(id: string) {
        const res = await api.get<{ success: boolean } & Record<string, unknown>>(
            `/enlist/war-room/${encodeURIComponent(id)}`
        );
        return unwrap<Record<string, unknown>>(res);
    },

    async reinforce(id: string) {
        const res = await api.post<Record<string, unknown>>(
            `/enlist/war-room/${encodeURIComponent(id)}/reinforce`,
            {}
        );
        return unwrap<Record<string, unknown>>(res);
    },

    async getSchedule() {
        const res = await api.get<{ success: boolean; games: unknown[] }>('/enlist/schedule');
        return unwrap<{ games: unknown[] }>(res);
    },

    async getChronicle() {
        const res = await api.get<{ success: boolean; latest: unknown; weeks: unknown[] }>(
            '/enlist/chronicle'
        );
        return unwrap<{ latest: unknown; weeks: unknown[] }>(res);
    },

    async getDrives() {
        const res = await api.get<{ success: boolean; drives: unknown[] }>('/enlist/drives');
        return unwrap<{ drives: unknown[] }>(res);
    },

    async checkin() {
        const res = await api.post<{ success: boolean; soldier: Soldier }>('/enlist/checkin', {});
        return unwrap<{ soldier: Soldier }>(res);
    },

    async reenlist(team_id: number) {
        const res = await api.post<{ success: boolean; soldier: Soldier }>('/enlist/reenlist', {
            team_id,
        });
        return unwrap<{ soldier: Soldier }>(res);
    },

    async moveHome(payload: { zip?: string; lat?: number; lng?: number; county_fips?: string }) {
        const res = await api.put<{ success: boolean; soldier: Soldier }>('/enlist/home', payload);
        return unwrap<{ soldier: Soldier }>(res);
    },

    async updateProfile(payload: {
        battle_cry?: string;
        profile_links?: string[];
        avatar_url?: string;
    }) {
        const res = await api.put<{ success: boolean; soldier: Soldier }>('/enlist/profile', payload);
        return unwrap<{ soldier: Soldier }>(res);
    },

    async sponsor(banner_text: string, county_fips?: string) {
        const res = await api.post<{ success: boolean; sponsor: unknown }>('/enlist/sponsor', {
            banner_text,
            county_fips,
        });
        return unwrap<{ sponsor: unknown }>(res);
    },

    async createBattalion(payload: {
        name: string;
        kind?: 'squad' | 'bar_hq';
        place_name?: string;
        county_fips?: string;
    }) {
        const res = await api.post<{ success: boolean; battalion: unknown }>('/enlist/battalions', payload);
        return unwrap<{ battalion: unknown }>(res);
    },

    async getBattalion(slug: string) {
        const res = await api.get<{ success: boolean; battalion: unknown }>(
            `/enlist/battalions/${encodeURIComponent(slug)}`
        );
        return unwrap<{ battalion: unknown }>(res);
    },

    async joinBattalion(id: number) {
        const res = await api.post<{ success: boolean; joined?: boolean }>(
            `/enlist/battalions/${id}/join`,
            {}
        );
        return unwrap<{ joined?: boolean }>(res);
    },

    async trackRecruiter(code: string) {
        const res = await api.post<{ success: boolean; ok?: boolean; code?: string }>(
            `/enlist/r/${encodeURIComponent(code)}`,
            {}
        );
        return unwrap<{ ok?: boolean; code?: string }>(res);
    },

    async rebellion() {
        const res = await api.post<{ success: boolean; checkout: CheckoutDescriptor }>(
            '/enlist/rebellion',
            {}
        );
        return unwrap<{ checkout: CheckoutDescriptor }>(res);
    },

    async buyMedal(medal_sku: string) {
        const res = await api.post<{ success: boolean; checkout: CheckoutDescriptor }>(
            '/enlist/medals/buy',
            { medal_sku }
        );
        return unwrap<{ checkout: CheckoutDescriptor }>(res);
    },

    dogTagUrl(handle: string) {
        const base = process.env.REACT_APP_API_URL || '/api/v2';
        return `${base}/og/dog-tag/${encodeURIComponent(handle)}.png`;
    },
};
